/* eslint-disable react-hooks/exhaustive-deps */
import {
  faEnvelope,
  faFolder,
  faReceipt,
  faTimesCircle,
  faTrashAlt,
  faUserTie
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { Formik, FormikErrors, setNestedObjectValues } from "formik";
import { cloneDeep } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { Autosave } from "react-autosave";
import { Alert, Badge, Button, Col, Container, Row, Tab, Tabs } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import { AppRulesContext } from "../../App";
import ITRFilingApi from "../../api/ITRFilingApi";
import PurchasePlanApi from "../../api/PurchasePlanApi";
import FASvgIcon from "../../components/FASvgIcon";
import GetExpertCard from "../../components/GetExpertCard";
import GuardedComponent from "../../components/GuardedComponent";
import PriceDisplay from "../../components/PriceDisplay";
import RazorPayPayment from "../../components/RazorpayPayment";
import TrackFilingStatus from "../../components/itr-filing/TrackFilingStatus";
import { ConfirmationModal } from "../../components/modals";
import AssignExpertFormModal from "../../components/modals/AssignExpertFormModal";
import { LoadingIndicator, NothingAvailable, OverlaySpinner } from "../../components/placeholders";
import ITRSingleFilingMDT from "../../components/sidebar/ITRSingleFilingMDT";
import {
  DeductionsTab,
  IncomeDetailsTab,
  PersonalDetailsTab,
  ReviewAndFileTab
} from "../../components/tabs/itr-filing";
import { AppAuthRoles } from "../../config/app-constants";
import { ITR1FormData, ITR1Mapping, TrackEFilingStatus } from "../../config/filingConstants";
import {
  EActionItem,
  EToastvariant,
  IActiveMDTTabs,
  IActiveTabs,
  IAssignFiling,
  ICreateEFPlanTxn,
  IFormITR1
} from "../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../redux/Store";
import {
  autoSaveChanges,
  getFilingDetails,
  getPlanList,
  ITRFilingSelector,
  MaxPlansAmountSelector,
  setCurrentFilingExpertData
} from "../../redux/slice/ITRFilingSlice";
import { loggedInUserSelector } from "../../redux/slice/LoggedInUserSlice";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";
import { FilingFormInitValues, FilingValidationSchema } from "./efiling.config";

type Props = {};

const ITRFiling: React.FC<Props> = (props: Props) => {
	const { filingUUID } = useParams();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { rulesSvc } = useContext(AppRulesContext);

	const {
		CFLoadingStatus,
		currentFilingUUID,
		currentFilingData,
		currentFilingPlan,
		isFilingReadOnly,
		currentFilingExpertData
	} = useAppSelector(ITRFilingSelector);
	const { loggedInUser } = useAppSelector(loggedInUserSelector);
	const maxPlanAmount = useAppSelector(MaxPlansAmountSelector);

	// update the url when "add itr" is opened to make similar to "edit itr"
	// const [ITRFilingUUID, setITRFilingUUID] = useState<string | null>(
	//   filingUUID || currentFilingUUID || null
	// );
	const [initFormValues, setInitFormValues] = useState(FilingFormInitValues);
	const [activeTab, setActiveTab] = useState<IActiveTabs>("personal-details");
	const [activeMDTTab, setActiveMDTTab] = useState<IActiveMDTTabs>(undefined);
	const [showAssignExpert, setShowAssignExpert] = useState<boolean>(false);
	const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
	const [isPerformingAction, setIsPerformingAction] = useState<EActionItem>(EActionItem.NULL);
	const [selectedPlanData, setSelectedPlanData] = useState<ICreateEFPlanTxn | null>(null);
	const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());
	const [validationErrorMsg, setValidationErrorMsg] = useState<string>("");
	const [allValidationError, setAllValidationError] = useState<FormikErrors<IFormITR1>>({});

	// // when user refresh the browser after adding some data in ITR form
	// // then convert add form into edit form and get the ITR details using filingUUID
	// useEffect(() => {
	//   if (!filingUUID && currentFilingUUID) {
	//     // set the new UUID when add itr "auto save" happens
	//     setITRFilingUUID(() => currentFilingUUID);
	//     // replace the old url "/itr-filings/0" with "/itr-filings/filingUUID"
	//     window.history.replaceState({}, "", `/itr-filings/${currentFilingUUID}`);
	//   }
	// }, [currentFilingUUID]);

	useEffect(() => {
		if (filingUUID) {
			dispatch(getFilingDetails(filingUUID));
			dispatch(getPlanList());
		}
	}, [filingUUID, currentTime]);

	useEffect(() => {
		const formData = cloneDeep(currentFilingData?.efilingData) as any;
		setInitFormValues((prev) => ({ ...prev, ...formData }));
	}, [currentFilingData, currentTime]);

	const handleAssignExpert = async (values: IAssignFiling) => {
		try {
			if (!currentFilingUUID) {
				return;
			}

			setIsPerformingAction(EActionItem.ASSIGN);
			const fullName = `${values.assignedToData?.fullName || ""}`;
			const _values = {
				efilingUuid: currentFilingUUID,
				userUuid: values?.assign_to
			};
			const filingApi = ITRFilingApi.getInstance();
			const { data } = await filingApi.assignExpert(_values);
			dispatch(setCurrentFilingExpertData(data));
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Success",
					message: `An expert assigned to ${fullName} successfully.`,
					variant: EToastvariant.Success
				})
			);
			setCurrentTime(() => new Date().getTime());
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setIsPerformingAction(EActionItem.NULL);
			setShowAssignExpert(false);
		}
	};

	const handleRemoveAssignedExpert = async () => {
		try {
			if (!currentFilingExpertData?.uuid) {
				return;
			}
			setIsPerformingAction(EActionItem.DELETE);
			const filingApi = ITRFilingApi.getInstance();
			await filingApi.deleteAssignFilingByUuIds([currentFilingExpertData.uuid]);

			setCurrentTime(() => new Date().getTime());
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Removed",
					message: "Assigned expert removed successfully.",
					variant: EToastvariant.Success
				})
			);
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setIsPerformingAction(EActionItem.NULL);
			setShowConfirmation(false);
		}
	};

	const handleFormSubmit = (values: IFormITR1) => {
		try {
			const updatedFormData = UtilService.updateSchemaData(values, ITR1FormData, ITR1Mapping);
			// console.log("updatedFormData values ", updatedFormData);
			// const validate = AJV.compile(PersonalInfoSchema, true);
			// const isValid = validate(updatedObj.ITR.ITR1.PersonalInfo);
			// console.log("isValid ", isValid);
			// if (!isValid) console.log("isValid errors ", validate.errors);
		} catch (error) {
			console.log("submit error ", error);
		}
	};

	const handlePendingPayment = async () => {
		try {
			if (!currentFilingPlan) {
				return;
			}

			setIsPerformingAction(() => EActionItem.CREATE);

			const planData: ICreateEFPlanTxn = {
				efilingUuid: currentFilingPlan.efilingUuid,
				planUuid: currentFilingPlan.planData.uuid
			};

			const plansApi = PurchasePlanApi.getInstance();
			const { subtotal } = await plansApi.createEfilingPlan(planData);

			if (subtotal === 0) {
				await plansApi.createEfilingPlanTransaction(planData);
				dispatch(
					showToastMsg({
						isVisible: true,
						title: "Success",
						message: "Success",
						variant: EToastvariant.Success
					})
				);
			} else {
				setSelectedPlanData(() => planData);
			}
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setIsPerformingAction(() => EActionItem.NULL);
		}
	};

	const getFileTrackingStatus = (status: string, userUuid: string) => {
		let _status = "";
		if (loggedInUser?.uuid === userUuid) {
			if (status === "EFILING_STATUS_REQUESTED_DETAILS") {
				_status = `${TrackEFilingStatus["EFILING_STATUS_REQUESTED_DETAILS_RECEIVED"]?.label}`;
			} else if (status === "EFILING_STATUS_RECEIVED_DETAILS") {
				_status = `${TrackEFilingStatus["EFILING_STATUS_DETAILS_SENT"]?.label}`;
			} else {
				_status = `${TrackEFilingStatus[status]?.label}`;
			}
		}

		return _status;
	};

	// to show badge Awaiting a Tax Expert
	const showAwaitingTaxExpert =
		currentFilingPlan &&
		!currentFilingPlan?.inactive &&
		currentFilingPlan?.planData?.type !== "self" &&
		!currentFilingExpertData &&
		loggedInUser?.role !== AppAuthRoles.ADMIN;

	// current file belong to the creator or not (in case of user and expert)
	const isFileOwner = loggedInUser?.uuid === currentFilingData?.userUuid;

	// determine should show the expert card or not (floating on right side)
	const showExpertCard =
		!currentFilingData?.completedAt &&
		loggedInUser?.role !== AppAuthRoles.ADMIN &&
		loggedInUser?.uuid === currentFilingData?.userUuid &&
		!currentFilingPlan?.planData;

	const showTrackStatusBadge = rulesSvc.UserAndExpertStatusBadge.permissions?.includes(
		currentFilingData?.status
	);

	const showAdminAssignTaxExpert =
		loggedInUser?.role === AppAuthRoles.ADMIN &&
		!currentFilingExpertData &&
		!currentFilingPlan?.inactive &&
		currentFilingPlan?.planData?.type === "assisted";

	return (
		<>
			<Formik
				enableReinitialize={true}
				initialValues={initFormValues}
				validationSchema={FilingValidationSchema}
				onSubmit={handleFormSubmit}
			>
				{({ values, errors, setTouched, validateForm }) => (
					<>
						<Container className="w-1170">
							{CFLoadingStatus === "PENDING" ? (
								<LoadingIndicator />
							) : CFLoadingStatus === "REJECTED" ? (
								<div className="text-center my-5">
									<NothingAvailable
										title="Something Went Wrong"
										message="Failed to fetch data from the server. Please try again later."
										btnTitle="Go to Home"
										onBtnPress={() => navigate("/", { replace: true })}
										btnRules={rulesSvc.getMyProfile}
									/>
								</div>
							) : (
								<>
									<Autosave
										data={values}
										onSave={() =>
											!isFilingReadOnly && filingUUID
												? dispatch(autoSaveChanges({ formData: values, filingUUID: filingUUID }))
												: null
										}
										saveOnUnmount={false}
										interval={3000}
									/>
									<Row className="gutter-20">
										<Col>
											{/* for admin, type assisted, payment done then show */}
											{showAdminAssignTaxExpert ? (
												<Alert variant="warning" className="mb-4 justify-content-between">
													<h4 className="alert-title">
														Type: {currentFilingPlan?.planData?.title || ""}
													</h4>
													<Button variant="warning" onClick={() => setShowAssignExpert(true)}>
														<FASvgIcon icon={faUserTie} />
														Assign Tax Expert
													</Button>
												</Alert>
											) : null}

											{/* for all */}
											{currentFilingExpertData &&
											loggedInUser?.uuid !== currentFilingExpertData?.uuid ? (
												<Alert variant="warning" className="mb-4 justify-content-between">
													<h4 className="alert-title d-flex align-items-center flex-wrap gap-2">
														Type: {currentFilingPlan?.planData?.title || ""}
														<>
															&nbsp;
															{loggedInUser?.uuid === currentFilingData?.userUuid &&
																Number(
																	currentFilingPlan?.planData?.pricingRule[0]?.pricePerFile
																) !== maxPlanAmount && (
																	<Button
																		variant="primary"
																		size="sm"
																		onClick={() =>
																			navigate(`/itr-filings/${currentFilingUUID}/assisted/plans`, {
																				state: { sourceScreen: "assisted" }
																			})
																		}
																	>
																		Upgrade Plan
																	</Button>
																)}
														</>
													</h4>

													{loggedInUser?.role === AppAuthRoles.ADMIN ? (
														<Button
															variant="transparent"
															className="px-0"
															onClick={() => setShowConfirmation(true)}
														>
															Tax Expert: {currentFilingExpertData?.User?.fullName}
															<span className="text-danger">
																<FASvgIcon icon={faTimesCircle} />
															</span>
														</Button>
													) : (
														<span className="px-3 leading-para mb-0 fw-bold">
															Tax Expert: {currentFilingExpertData?.User?.fullName}
														</span>
													)}
												</Alert>
											) : null}

											{/* for user or expert, when payment is not completed */}
											{/* TODO: properly check for payment cancel status  */}
											{currentFilingPlan &&
											currentFilingPlan?.inactive &&
											currentFilingPlan?.planData?.payableAmount > 0 ? (
												<GuardedComponent rules={rulesSvc.createAndUpdateEFiling} behavior="hide">
													<Alert variant="warning" className="mb-4 justify-content-between">
														<h4 className="alert-title">
															Type: {currentFilingPlan?.planData?.title || ""}
															<Badge bg="warning" className="ms-3" pill={true}>
																Payment Pending
															</Badge>
														</h4>
														{loggedInUser?.role === AppAuthRoles.ADMIN ? (
															<p className="leading-para mb-0">
																Amount to be paid:&nbsp;
																<PriceDisplay
																	price={currentFilingPlan?.planData?.payableAmount || "0"}
																/>
															</p>
														) : (
															<Button variant="primary" onClick={() => handlePendingPayment()}>
																Pay Now
																<PriceDisplay
																	price={currentFilingPlan?.planData?.payableAmount || "0"}
																/>
															</Button>
														)}
													</Alert>
												</GuardedComponent>
											) : null}

											<div className="title-wrap">
												<div className="title-wrap--heading">
													<h1>ITR Filing</h1>
												</div>
												<div className="title-wrap--btns gap-3">
													{currentFilingExpertData &&
														currentFilingPlan &&
														!currentFilingPlan?.inactive &&
														currentFilingPlan?.planData?.type === "assisted" && (
															<>
																<Button
																	variant="outline-warning"
																	className="btn-w-180"
																	onClick={() => setActiveMDTTab("messages")}
																>
																	<FASvgIcon icon={faEnvelope} />
																	Messages
																</Button>
																<Button
																	variant="outline-primary"
																	className="btn-w-180"
																	onClick={() => setActiveMDTTab("documents")}
																>
																	<FASvgIcon icon={faFolder} />
																	Documents
																</Button>
															</>
														)}

													{currentFilingPlan?.efilingPlanTransaction &&
														(loggedInUser?.role === AppAuthRoles.ADMIN ||
															loggedInUser?.uuid === currentFilingData?.userUuid) && (
															<Button
																variant="outline-success"
																className="btn-w-180"
																onClick={() => setActiveMDTTab("transaction")}
															>
																<FASvgIcon icon={faReceipt} />
																Transaction Info
															</Button>
														)}

													{!currentFilingData?.efilingPlan?.efilingPlanTransaction ? (
														<GuardedComponent
															rules={rulesSvc.createAndUpdateEFiling}
															behavior="hide"
														>
															<Button
																variant="outline-primary"
																className="btn-w-250"
																onClick={() =>
																	navigate(`/itr-filings/${currentFilingUUID}/assisted/plans`, {
																		state: { sourceScreen: "assisted" }
																	})
																}
															>
																<FASvgIcon icon={faUserTie} />
																Get Expert Assistance
															</Button>
														</GuardedComponent>
													) : null}
												</div>
											</div>

											{currentFilingData &&
											((isFileOwner && showTrackStatusBadge) || showAwaitingTaxExpert) ? (
												<div className="sub-title-wrap">
													<div className="sub-title-wrap--heading">
														{showTrackStatusBadge ? (
															<Badge
																pill={true}
																className={classNames(
																	"status-badge primary-para",
																	currentFilingData?.status
																		? `text-${
																				TrackEFilingStatus[currentFilingData?.status]
																					?.bgTextColor || ""
																		  }`
																		: null
																)}
																bg={
																	TrackEFilingStatus[currentFilingData?.status || " "]?.variant ||
																	""
																}
															>
																{getFileTrackingStatus(
																	currentFilingData.status,
																	currentFilingData.userUuid
																)}
															</Badge>
														) : null}

														{showAwaitingTaxExpert ? (
															<Badge bg="warning" pill={true} className="primary-para">
																Awaiting a Tax Expert
															</Badge>
														) : null}
													</div>
												</div>
											) : null}

											{rulesSvc.canAccess(rulesSvc.AdminEFilingStatus) ||
											rulesSvc.canAccess(rulesSvc.TaxExpertEFilingStatus) ? (
												<div className="sub-title-wrap justify-content-end">
													<div className="sub-title-wrap--actions">
														<TrackFilingStatus />
													</div>
												</div>
											) : null}

											{isFilingReadOnly && loggedInUser?.role !== AppAuthRoles.ADMIN && (
												<>
													<p className="fw-bold text-danger">
														Please note that you have read-only access to the form. Our tax
														professional will fill it out in. <br />
														If you would like to make changes or have any queries, please utilize
														the Messages button in the top right corner.
													</p>
												</>
											)}
											<div className="info-missing">
												{validationErrorMsg && Object.keys(errors).length > 0 ? (
													<>
														<Alert variant="danger" className="mb-4">
															<h2 className="alert-title">
																Information Missing
																<small className="leading-para">{validationErrorMsg}</small>
															</h2>
														</Alert>
													</>
												) : null}
											</div>
											<Tabs
												activeKey={activeTab}
												id="efiling-tabs"
												className="border-nav-tabs mt-5"
												onSelect={(activeKey) => {
													switch (activeKey) {
														case "personal-details":
															setActiveTab("personal-details");
															break;
														case "income-details":
															setActiveTab("income-details");
															break;
														case "deductions":
															setActiveTab("deductions");
															break;
														case "review-file":
															setActiveTab("review-file");
															break;
													}
												}}
											>
												<Tab eventKey="personal-details" title="Personal details">
													<PersonalDetailsTab
														handleNaviagteTab={(tabName) => {
															setActiveTab(tabName);
															UtilService.scrollToTop();
														}}
														allValidationError={allValidationError}
													/>
												</Tab>
												<Tab eventKey="income-details" title="Income details">
													<IncomeDetailsTab
														handleNaviagteTab={(tabName) => {
															setActiveTab(tabName);
															UtilService.scrollToTop();
														}}
													/>
												</Tab>
												<Tab eventKey="deductions" title="Deductions">
													<DeductionsTab
														handleNaviagteTab={(tabName) => {
															setActiveTab(tabName);
															UtilService.scrollToTop();
														}}
														allValidationError={allValidationError}
													/>
												</Tab>
												<Tab eventKey="review-file" title="Review & File">
													<ReviewAndFileTab
														handleNaviagteTab={(tabName) => setActiveTab(tabName)}
														activeTab={activeTab}
														validationErrorMsg={async () => {
															const validationErrors = await validateForm();
															if (Object.keys(validationErrors).length === 0) {
																setValidationErrorMsg("");
															} else {
																setAllValidationError(validationErrors);
																setTouched(setNestedObjectValues(validationErrors, true));
																setValidationErrorMsg(
																	"Please fix all the missing/incorrect information to continue to e-file"
																);
																return;
															}
														}}
													/>
												</Tab>
											</Tabs>
										</Col>

										{showExpertCard ? (
											<GuardedComponent rules={rulesSvc.createAndUpdateEFiling} behavior="hide">
												<Col md="auto" className="d-none d-lg-block">
													<GetExpertCard />
												</Col>
											</GuardedComponent>
										) : null}
									</Row>
								</>
							)}
						</Container>
					</>
				)}
			</Formik>

			<ITRSingleFilingMDT
				showCanvas={!!activeMDTTab}
				activeTab={activeMDTTab}
				handleCloseBtnPress={() => setActiveMDTTab(undefined)}
			/>

			<AssignExpertFormModal
				showModal={showAssignExpert}
				onYesBtnClick={(values) => handleAssignExpert(values)}
				handleModalClose={() => setShowAssignExpert(false)}
				isProcessing={isPerformingAction === EActionItem.ASSIGN}
			/>

			{showConfirmation && (
				<ConfirmationModal
					showModal={showConfirmation}
					iconColorClass="text-danger"
					title="Remove Tax Expert"
					message={<p>Please confirm the removal of Tax Expert.</p>}
					onYesBtnClick={() => handleRemoveAssignedExpert()}
					onNoBtnClick={() => {
						setShowConfirmation(false);
					}}
					icon={faTrashAlt}
					onYesBtnText="Remove"
					isProcessing={isPerformingAction === EActionItem.DELETE}
				/>
			)}

			{isPerformingAction === EActionItem.CREATE && <OverlaySpinner />}

			{Boolean(selectedPlanData) && (
				<RazorPayPayment
					loadRazorpay={Boolean(selectedPlanData)}
					filingPlanData={selectedPlanData}
					handleOnPaymentSuccess={() => {
						setSelectedPlanData(() => null);
						// refresh the page after payment
						setCurrentTime(() => new Date().getTime());
						navigate("/payment-successful");
					}}
					handleOnPaymentCancel={() => setSelectedPlanData(() => null)}
					handleErrorOnInit={() => {
						setSelectedPlanData(() => null);
					}}
				/>
			)}
		</>
	);
};

export default ITRFiling;
